if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */

if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.documentElement = document.documentElement;
      this.cssVariableCache = {};
      this.pendingAnimationFrame = null;
      this.shouldRecalculateStickyOffset = true;
      this.shouldMeasureStaticMetrics = true;
      this.stickyOffset = 0;
      this.currentScrollY = 0;
      this.boundOnScroll = this.onScroll.bind(this);
      this.boundOnResize = debounce(this.onResize.bind(this), 100);
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.announcementBar = document.querySelector('.announcement-bar-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = this.querySelector('.mobile-toggle-wrapper');
      this.toggleButton = this.toggle?.querySelector('.mobile-toggle');
      this.mobileContactNumber = document.querySelector('.mobile-contact-number');
      this.contactInfoBar = document.querySelector('.contact-info-bar');
      this.searchBarIcon = document.querySelector('.thb-quick-search');
      this.mobileIconMenuTrigger = this.querySelector('.btn-menu-hamburger');

      // Signal that header.js owns the mobile icon bar wiring so app-site-custom.js can skip rebinding it.
      window.__thermoMobileIconBarHandled = true;

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.setMenuState(false);
          }
        }
      });
      this.isStickyHeaderActive = this.classList.contains('header-sticky--active');

      if (this.isStickyHeaderActive) {
        document.body.classList.add('header-sticky--active');
      }
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.setMenuState(!this.toggle.classList.contains('active'));
        });
      }

      if (this.mobileIconMenuTrigger) {
        this.mobileIconMenuTrigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!this.toggle) {
            return;
          }
          this.setMenuState(!this.toggle.classList.contains('active'));
        });
      }

      window.addEventListener('scroll', this.boundOnScroll, {
        passive: true
      });
      window.addEventListener('resize', this.boundOnResize, {
        passive: true
      });

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => {
          this.scheduleLayoutUpdate(true);
        });

        this.resizeObserver.observe(this);
        if (this.announcementBar) {
          this.resizeObserver.observe(this.announcementBar);
        }
      }

      this.currentScrollY = window.scrollY;
      this.scheduleLayoutUpdate(true);

      // Mobile Navigation transparent header support.
      setTimeout(() => {

        this.mobile_nav = document.querySelector('.header-mobile-navigation');

        if (this.mobile_nav && this.classList.contains('transparent--true')) {
          this.mobile_nav.classList.add('is-fixed');
        }
      }, 100);

      if (typeof window.matchMedia === 'function') {
        this.mobileHeaderMediaQuery = window.matchMedia('(max-width: 430px)');
        this.boundSyncOnResize = () => {
          this.syncMobileHeaderState(this.toggle?.classList.contains('active'));
          this.scheduleLayoutUpdate(true);
        };
        if (typeof this.mobileHeaderMediaQuery.addEventListener === 'function') {
          this.mobileHeaderMediaQuery.addEventListener('change', this.boundSyncOnResize);
        } else if (typeof this.mobileHeaderMediaQuery.addListener === 'function') {
          this.mobileHeaderMediaQuery.addListener(this.boundSyncOnResize);
        }
      }

      this.syncMobileHeaderState(this.toggle?.classList.contains('active'));
      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.boundOnScroll);
      window.removeEventListener('resize', this.boundOnResize);

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.pendingAnimationFrame) {
        window.cancelAnimationFrame(this.pendingAnimationFrame);
        this.pendingAnimationFrame = null;
      }

      if (this.mobileHeaderMediaQuery && this.boundSyncOnResize) {
        if (typeof this.mobileHeaderMediaQuery.removeEventListener === 'function') {
          this.mobileHeaderMediaQuery.removeEventListener('change', this.boundSyncOnResize);
        } else if (typeof this.mobileHeaderMediaQuery.removeListener === 'function') {
          this.mobileHeaderMediaQuery.removeListener(this.boundSyncOnResize);
        }
      }
    }
    setMenuState(menuOpen) {
      if (!this.toggle) {
        return;
      }

      if (menuOpen) {
        this.toggle.setAttribute('open', '');
        this.toggle.classList.add('active');
        document.body.classList.add('overflow-hidden');
        document.body.classList.add('mobile-menu-open');
        this.syncMobileHeaderState(true);
        this.scheduleLayoutUpdate(true);
        return;
      }

      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('mobile-menu-open');
      this.toggle.classList.remove('active');
      this.syncMobileHeaderState(false);
      this.scheduleLayoutUpdate(true);
      this.closeAnimation(this.toggle);
    }
    syncMobileHeaderState(menuOpen) {
      const isTinyScreen = !!(this.mobileHeaderMediaQuery && this.mobileHeaderMediaQuery.matches);

      if (!this.contactInfoBar || !this.mobileContactNumber) {
        return;
      }

      if (!isTinyScreen) {
        this.setInlineStyle(this.contactInfoBar, 'display', 'flex');
        this.setInlineStyle(this.contactInfoBar, 'contentVisibility', 'visible');
        this.setInlineStyle(this.mobileContactNumber, 'display', 'none');
        this.setInlineStyle(this.mobileContactNumber, 'contentVisibility', 'hidden');
        if (this.searchBarIcon) {
          this.setInlineStyle(this.searchBarIcon, 'display', 'inline-flex');
        }
        return;
      }

      if (menuOpen) {
        this.setInlineStyle(this.contactInfoBar, 'display', 'flex');
        this.setInlineStyle(this.contactInfoBar, 'contentVisibility', 'visible');
        this.setInlineStyle(this.mobileContactNumber, 'display', 'none');
        this.setInlineStyle(this.mobileContactNumber, 'contentVisibility', 'hidden');
        if (this.searchBarIcon) {
          this.setInlineStyle(this.searchBarIcon, 'display', 'none');
        }
      } else {
        this.setInlineStyle(this.contactInfoBar, 'display', 'none');
        this.setInlineStyle(this.contactInfoBar, 'contentVisibility', 'hidden');
        this.setInlineStyle(this.mobileContactNumber, 'display', 'flex');
        this.setInlineStyle(this.mobileContactNumber, 'contentVisibility', 'visible');
      }
    }
    onScroll() {
      this.currentScrollY = window.scrollY;
      this.scheduleLayoutUpdate(false, false);
    }
    onResize() {
      this.scheduleLayoutUpdate(true, true);
    }
    scheduleLayoutUpdate(recalculateStickyOffset, measureStaticMetrics = recalculateStickyOffset) {
      if (recalculateStickyOffset) {
        this.shouldRecalculateStickyOffset = true;
      }
      if (measureStaticMetrics) {
        this.shouldMeasureStaticMetrics = true;
      }

      if (this.pendingAnimationFrame) {
        return;
      }

      this.pendingAnimationFrame = window.requestAnimationFrame(() => {
        this.pendingAnimationFrame = null;
        this.updateLayoutMetrics();
      });
    }
    updateLayoutMetrics() {
      if (this.shouldRecalculateStickyOffset) {
        this.updateStickyOffset();
      }

      this.setStickyClass();
      this.setHeaderOffset();
      if (this.shouldMeasureStaticMetrics) {
        this.setAnnouncementHeight();
        this.setHeaderHeight();
        this.shouldMeasureStaticMetrics = false;
      }
    }
    updateStickyOffset() {
      this.stickyOffset = Math.round(this.getBoundingClientRect().top + window.scrollY);
      this.shouldRecalculateStickyOffset = false;
    }
    setStickyClass() {
      if (!this.isStickyHeaderActive) {
        return;
      }

      this.classList.toggle('is-sticky', this.currentScrollY >= this.stickyOffset && this.currentScrollY > 0);
    }
    setAnnouncementHeight() {
      if (!this.announcementBar) {
        return;
      }

      const height = Math.round(this.announcementBar.getBoundingClientRect().height);
      this.setCssVariable('--announcement-height', height + 'px');
    }
    setHeaderOffset() {
      if (!this.header_section) {
        return;
      }

      const offset = Math.round(this.header_section.getBoundingClientRect().top);
      this.setCssVariable('--header-offset', offset + 'px');
    }
    setHeaderHeight() {
      const height = Math.round(this.getBoundingClientRect().height);
      this.setCssVariable('--header-height', height + 'px');
    }
    setCssVariable(name, value) {
      if (this.cssVariableCache[name] === value) {
        return;
      }

      this.cssVariableCache[name] = value;
      this.documentElement.style.setProperty(name, value);
    }
    setInlineStyle(element, property, value) {
      if (!element || element.style[property] === value) {
        return;
      }
      element.style[property] = value;
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');
      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}

/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();

      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');
    }
    connectedCallback() {
      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      document.fonts.ready.then(function() {
        window.addEventListener('resize', debounce(function() {
          _this.resizeSubMenus();
        }, 100));
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.offsetWidth,
            total = w + l;

          if (total > window.innerWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
